import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import './styles/figma-design-tokens.css'
import './styles/react-native.css'
import './style.css'
import App from './App.vue'
import PcListSearchRow from '@/components/pc/PcListSearchRow.vue'
import { installClientErrorReporter } from '@/utils/clientErrorReporter'

// ─── 전역 이미지 에러 Fallback ────────────────────────────────────────────────
// 관리자/사용자가 업로드한 이미지가 없거나 깨진 경우 placeholder 자동 교체
// - data-profile-avatar 속성이 있는 img → ProfileAvatar.vue 자체 처리 (사람 아이콘), 여기선 제외
// - data-fallback-applied 속성이 이미 있는 img → 무한루프 방지
const IMG_PLACEHOLDER = `data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill="none">' +
  '<rect width="48" height="48" fill="#F5F5F5"/>' +
  '<rect x="8" y="10" width="32" height="28" rx="2" stroke="#CCCCCC" stroke-width="1.5"/>' +
  '<circle cx="18" cy="20" r="3" stroke="#CCCCCC" stroke-width="1.5"/>' +
  '<path d="M8 32l9-8 6 6 4-4 12 10" stroke="#CCCCCC" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>'
)}`

window.addEventListener('error', (e) => {
  if (!(e.target instanceof HTMLImageElement)) return
  const img = e.target as HTMLImageElement
  if (img.dataset.fallbackApplied || img.dataset.profileAvatar !== undefined) return
  img.dataset.fallbackApplied = 'true'
  /* 2026-10-02 10/1 §7-D8 — 빈 사진으로 덮으면 권한 오류(403)·없는 파일이 «원래 그런 화면» 처럼 보여
     포토게시판 회색(슬라이드 72) 같은 결함이 드러나지 않았다. 사용자에겐 그대로 빈 사진을 보이되,
     실패한 원래 주소를 표식으로 남겨 검사기·개발자가 찾을 수 있게 한다(개발 빌드는 콘솔에도) */
  img.dataset.imgFailed = img.currentSrc || img.src
  if (import.meta.env.DEV) console.warn('[이미지 실패]', img.dataset.imgFailed)
  img.src = IMG_PLACEHOLDER
}, true)
// ─────────────────────────────────────────────────────────────────────────────

// ─── 카카오 JS SDK 초기화 ────────────────────────────────────────────────────
// '카카오톡 보내기'(Kakao.Share) 전용. 키가 없거나 SDK 로드 실패 시 조용히 건너뛰고
// 각 화면의 폴백(navigator.share → 링크 복사)이 그대로 동작한다.
// ※ 서버 발송 알림톡(CJ mplace Agent)과는 무관한 별개 채널.
const kakaoJsKey = import.meta.env.VITE_KAKAO_JS_KEY as string | undefined
if (kakaoJsKey) {
  const kakao = (window as any).Kakao
  if (kakao && !kakao.isInitialized?.()) {
    try {
      kakao.init(kakaoJsKey)
    } catch (e) {
      // v12-ok: 카카오 SDK 초기화 실패 — 공유는 navigator.share·링크 복사 폴백으로 동작
      console.warn('Kakao SDK 초기화 실패 — 공유는 폴백으로 동작합니다', e)
    }
  }
}
// ─────────────────────────────────────────────────────────────────────────────

// React Native WebView 환경 감지
if (window.ReactNativeWebView) {
  document.body.classList.add('react-native-webview')
  console.log('📱 Running in React Native WebView')
  
  // JWT 토큰 확인 (디버깅용)
  setTimeout(() => {
    const accessToken = localStorage.getItem('accessToken')
    const refreshToken = localStorage.getItem('refreshToken')
    
    if (accessToken && refreshToken) {
      console.log('✅ JWT tokens found in localStorage')
      console.log('🔑 Access Token:', accessToken.substring(0, 20) + '...')
    } else {
      console.log('⚠️ No JWT tokens in localStorage')
    }
  }, 500)
} else {
  console.log('🌐 Running in web browser')
}

const app = createApp(App)
installClientErrorReporter(app)
// PC 목록 검색 공통 컴포넌트: 라우트 청크/캐시 불일치 시에도 해석 실패 방지
app.component('PcListSearchRow', PcListSearchRow)

app.use(createPinia())
app.use(router)

app.mount('#app')
